<template>
    <page
        class="dashboard-page"
        :busy="busy"
        :tabs="tabs"
        :compact-mobile-view="true"
        :can-add-tab="!!currentDashboard"
        :can-remove-tab="true"
        @add-tab="handleAddTab"
        @remove-tab="handleRemoveTab"
    >
        <div v-if="currentDashboard">
            <grid-layout
                ref="grid_layout"
                v-if="!currentDashboardTabWidgetsNullable"
                :class="{ unselectable: isDraggingWidget || isResizingWidget }"
                v-model:layout="internalCurrentDashboardTabWidgets"
                :col-num="gridLayoutColNum"
                :cols="{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }"
                :row-height="gridLayoutRowHeight"
                :responsive="true"
                :is-draggable="isLargeScreenOnLoad && isLargeScreen()"
                :is-resizable="false"
                :vertical-compact="true"
                :use-css-transforms="false"
                :margin="[gridLayoutHorizontalMargin, gridLayoutVerticalMargin]"
                @layout-ready="handleLayoutReady"
                @layout-updated="handleLayoutUpdated"
            >
                <component
                    v-for="tabWidget in internalCurrentDashboardTabWidgets"
                    :key="tabWidget.id"
                    :is="getTabWidgetComponent(tabWidget)"
                    :ready="ready"
                    :tab-widget="tabWidget"
                    @success-tab-widget-settings-update="handleSuccessTabWidgetSettingsUpdate"
                    :resizeEmitEven="resizeEmitEvent"
                />
                <grid-item
                    v-if="ready"
                    :x="0"
                    :y="currentDashboardTabNewWidgetY"
                    :w="12"
                    :h="10"
                    :i="generateWidgetID()"
                    :static="true"
                    :style="{ paddingBottom: gridLayoutVerticalMargin + 'px' }"
                >
                    <div class="add-widget-card h-100 min-height-100 d-flex justify-content-center align-items-center">
                        <b-button pill title="Add Widget" variant="primary" @click="handleAddWidgetClick">
                            <font-awesome-icon :icon="['fas', 'plus']" size="lg"></font-awesome-icon>
                        </b-button>
                    </div>
                </grid-item>
            </grid-layout>
        </div>
        <modal :id="removeDashboardTabModalID" title="Remove Dashboard Tab" @ok="deleteCurrentDashboardTabForRemove"
            >Are you sure you want to remove "{{ currentDashboardTabForRemoveName }}" tab?</modal
        >
        <dashboard-tab-details-modal-form
            :key="detailsDashboardTabModalKey"
            :modal-id="detailsDashboardTabModalID"
            :create="createDashboardTab"
            :initial-values="currentDashboardTabInitialValues"
            @success-create="handleSuccessDashboardTabCreate"
        />
        <add-widget-modal
            :modal-id="addWidgetModalID"
            :dashboard-tab-id="currentDashboardTab && currentDashboardTab.id"
            @success-create="handleSuccessDashboardTabWidgetCreate"
        />
    </page>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import { GridLayout, GridItem } from 'grid-layout-plus';
import API from '../../api';
import ArrivalsStatisticsPieChartCardGridItem from './widgets/arrivals-statistics-pie-chart-card/arrivals-statistics-pie-chart-card-grid-item.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { DASHBOARD_DASHBOARD_TAB_LIST_ENDPOINT } from '../../endpoints';
import DeparturesChartCardGridItem from './widgets/departures-chart-card/departures-chart-card-grid-item.vue';
import ArrivalsChartCardGridItem from './widgets/arrivals-chart-card/arrivals-chart-card-grid-item.vue';
import { isLargeScreen } from '../../utils/screens';
import { formatResponseError } from '../../utils/api';
import Modal from '../generic/modal/modal.vue';
import Page from '../generic/page/page.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import AirbnbSyncErrorsTableCardGridItem from './widgets/airbnb-sync-errors-table-card/airbnb-sync-errors-table-card-grid-item.vue';
import InquiriesChartCardGridItem from './widgets/inquiries-chart-card/inquiries-chart-card-grid-item.vue';
import BalancesDueReservationsTableCardGridItem from './widgets/balances-due-reservations-table-card/balances-due-reservations-table-card-grid-item.vue';
import BookingsChartCardGridItem from './widgets/bookings-chart-card/bookings-chart-card-grid-item.vue';
import BookingRevenueChartCardGridItem from './widgets/booking-revenue-chart-card/booking-revenue-chart-card-grid-item.vue';
import CancellationsTableCardGridItem from './widgets/cancellations-table-card/cancellations-table-card-grid-item.vue';
import FailedTransactionsTableCardGridItem from './widgets/failed-transactions-table-card/failed-transactions-table-card-grid-item.vue';
import GuestTransactionsTableCardGridItem from './widgets/guest-transactions-table-card/guest-transactions-table-card-grid-item.vue';
import IssuedPoliciesChartCardGridItem from './widgets/issued-policies-chart-card/issued-policies-chart-card-grid-item.vue';
import InquiriesTableCardGridItem from './widgets/inquiries-table-card/inquiries-table-card-grid-item.vue';
import ReservationsPieChartTableCardGridItem from './widgets/reservations-pie-chart-table-card/reservations-pie-chart-table-card-grid-item.vue';
import GuestEmailBouncesTableCardGridItem from './widgets/guest-email-bounces-table-card/guest-email-bounces-table-card-grid-item.vue';
import ICalendarConflictsTableCardGridItem from './widgets/icalendar-conflicts-table-card/icalendar-conflicts-table-card-grid-item.vue';
import LapsedSubscribersTableCardGridItem from './widgets/lapsed-subscribers-table-card/lapsed-subscribers-table-card-grid-item.vue';
import NewSubscribersTableCardGridItem from './widgets/new-subscribers-table-card/new-subscribers-table-card-grid-item.vue';
import PendingReservationsTableCardGridItem from './widgets/pending-reservations-table-card/pending-reservations-table-card-grid-item.vue';
import RefundDueReservationsTableCardGridItem from './widgets/refund-due-reservations-table-card/refund-due-reservations-table-card-grid-item.vue';
import SubscriptionRevenueChartCardGridItem from './widgets/subscription-revenue-chart-card/subscription-revenue-chart-card-grid-item.vue';
import TrialSubscribersChartCardGridItem from './widgets/trial-subscribers-chart-card/trial-subscribers-chart-card-grid-item.vue';
import TodoListCardGridItem from './widgets/todo-list-card/todo-list-card-grid-item.vue';
import NoteListCardGridItem from './widgets/note-list-card/note-list-card-grid-item.vue';
import PaidSubscribersChartCardGridItem from './widgets/paid-subscribers-chart-card/paid-subscribers-chart-card-grid-item.vue';
import StatisticsCardGridItem from './widgets/statistics-card/statistics-card-grid-item.vue';
import BookingMethodChartCardGridItem from './widgets/booking-method-chart-card/booking-method-chart-card-grid-item.vue';
import AllArrivalsTableCardGridItem from './widgets/all-arrivals-table-card/all-arrivals-table-card-grid-item.vue';
import AllDeparturesTableCardGridItem from './widgets/all-departures-table-card/all-departures-table-card-grid-item.vue';
import ChannexUsageChartCardGridItem from './widgets/channex-usage-chart-card/channex-usage-chart-card-grid-item.vue';
import AverageTripDurationChartCardGridItem from './widgets/average-trip-duration-chart-card/average-trip-duration-chart-card-grid-item.vue';
import StaylioInstallBaseChartCardGridItem from './widgets/staylio-install-base-chart-table-card/staylio-install-base-chart-card-grid-item.vue';
import WeatherCardGridItem from './widgets/weather-card/weather-card-grid-item.vue';
import StaylioAdoptionRateChartCardGridItem from './widgets/staylio-adoption-rate-chart-card/staylio-adoption-rate-chart-card-grid-item.vue';
import StaylioInstallBaseHistoryChartCardGridItem from './widgets/staylio-install-base-history-chart-table-card/staylio-install-base-history-chart-card-grid-item.vue';
import StaylioPushNotificationMetricsChartCardGridItem from './widgets/staylio-push-notification-metrics-chart-card/staylio-push-notification-metrics-chart-card-grid-item.vue';

import {
    GRID_LAYOUT_COL_NUM,
    GRID_LAYOUT_ROW_HEIGHT,
    GRID_LAYOUT_HORIZONTAL_MARGIN,
    GRID_LAYOUT_VERTICAL_MARGIN,
    WIDGET_SYSNAME_COMPONENT_NAME_MAPPING,
    generateWidgetID,
} from './widgets/helpers';
import AddWidgetModal from './add-widget-modal.vue';
import DashboardTabDetailsModalForm from './dashboard-tab-details-modal-form.vue';

export default {
    name: 'Dashboard',
    components: {
        AirbnbSyncErrorsTableCardGridItem,
        ArrivalsStatisticsPieChartCardGridItem,
        BalancesDueReservationsTableCardGridItem,
        DashboardTabDetailsModalForm,
        GridLayout: GridLayout,
        GridItem: GridItem,
        Modal,
        Page,
        ResponsiveButton,
        BookingsChartCardGridItem,
        BookingRevenueChartCardGridItem,
        CancellationsTableCardGridItem,
        FailedTransactionsTableCardGridItem,
        GuestTransactionsTableCardGridItem,
        GuestEmailBouncesTableCardGridItem,
        ICalendarConflictsTableCardGridItem,
        IssuedPoliciesChartCardGridItem,
        InquiriesChartCardGridItem,
        InquiriesTableCardGridItem,
        ReservationsPieChartTableCardGridItem,
        LapsedSubscribersTableCardGridItem,
        NewSubscribersTableCardGridItem,
        PendingReservationsTableCardGridItem,
        RefundDueReservationsTableCardGridItem,
        SubscriptionRevenueChartCardGridItem,
        TrialSubscribersChartCardGridItem,
        WeatherCardGridItem,
        AddWidgetModal,
        TodoListCardGridItem,
        NoteListCardGridItem,
        StatisticsCardGridItem,
        DeparturesChartCardGridItem,
        PaidSubscribersChartCardGridItem,
        BookingMethodChartCardGridItem,
        AllArrivalsTableCardGridItem,
        AllDeparturesTableCardGridItem,
        ChannexUsageChartCardGridItem,
        AverageTripDurationChartCardGridItem,
        StaylioInstallBaseChartCardGridItem,
        StaylioAdoptionRateChartCardGridItem,
        StaylioInstallBaseHistoryChartCardGridItem,
        StaylioPushNotificationMetricsChartCardGridItem,
        ArrivalsChartCardGridItem,
    },
    mixins: [removeScrollMixin],
    computed: {
        ...mapGetters('dashboard', [
            'currentDashboard',
            'currentDashboardTab',
            'currentDashboardTabs',
            'currentDashboardTabWidgets',
            'currentDashboardTabWidgetsNullable',
            'currentDashboardTabNewWidgetY',
            'dashboardTabByID',
            'isDraggingWidget',
            'isResizingWidget',
        ]),
        currentRouteDashboard() {
            return parseInt(this.$route.query.dashboard);
        },
        currentRouteTab() {
            return parseInt(this.$route.query.tab);
        },
        currentDashboardTabForRemoveName() {
            if (this.currentDashboardTabForRemoveID) {
                const dashboardTab = this.dashboardTabByID(this.currentDashboardTabForRemoveID);
                if (dashboardTab) {
                    return dashboardTab.name;
                }
            }
            return 'this tab';
        },
        currentDashboardTabInitialValues() {
            return {
                dashboard: this.currentDashboard && this.currentDashboard.id,
            };
        },
        internalCurrentDashboardTabWidgets: {
            get() {
                return this.currentDashboardTabWidgets || [];
            },
            set(tabWidgets) {
                this.$store.commit('dashboard/updatedCurrentDashboardTabWidgets', tabWidgets);
            },
        },
        tabs() {
            const currentDashboardID = this.currentDashboard && this.currentDashboard.id;
            return this.currentDashboardTabs.map((dashboardTab) => ({
                id: dashboardTab.id,
                title: dashboardTab.name,
                route: this.$route.path + `?tab=${dashboardTab.id}&dashboard=${currentDashboardID}`,
                active: !!this.currentDashboardTab && this.currentDashboardTab.id === dashboardTab.id,
            }));
        },
    },
    data() {
        return {
            ready: false,
            busy: false,
            gridLayoutColNum: GRID_LAYOUT_COL_NUM,
            gridLayoutRowHeight: GRID_LAYOUT_ROW_HEIGHT,
            gridLayoutHorizontalMargin: GRID_LAYOUT_HORIZONTAL_MARGIN,
            gridLayoutVerticalMargin: GRID_LAYOUT_VERTICAL_MARGIN,
            detailsDashboardTabModalKey: 0,
            currentDashboardTabForRemoveID: null,
            addWidgetModalID: 'add-widget-modal',
            detailsDashboardTabModalID: 'details-dashboard-tab-modal',
            removeDashboardTabModalID: 'remove-dashboard-tab-modal',
            isLargeScreenOnLoad: isLargeScreen(),
        };
    },
    provide() {
        return {
            resizeEmitEvent: this.resizeEmitEvent,
        };
    },
    mounted() {
        (async () => {
            this.busy = true;
            try {
                await this.fetchDashboard();
            } finally {
                this.busy = false;
            }
        })();
    },
    updated() {
        if (this.$route.query.show_ticket_created === '1') {
            this.$router.replace({ query: {} });
            this.successToast({
                text: `Your request has been submitted, support will be with you shortly.`,
            });
        }
    },
    methods: {
        handleAddWidgetClick() {
            this.$bvModal.show(this.addWidgetModalID);
        },
        getTabWidgetComponent(tabWidget) {
            return WIDGET_SYSNAME_COMPONENT_NAME_MAPPING[tabWidget.widget.sysname];
        },
        async fetchDashboard() {
            try {
                await this.$store.dispatch('dashboard/fetchCurrentDashboard', {
                    id: this.currentRouteDashboard,
                });
            } catch (err) {
                this.errorToast({ text: 'Could not load dashboard.' });
            }

            if (this.currentRouteTab) {
                await this.$store.dispatch('dashboard/setCurrentDashboardTab', {
                    id: this.currentRouteTab,
                });
            } else {
                await this.$store.dispatch('dashboard/setFirstCurrentDashboardTab');
            }
            if (this.currentDashboardTab && this.currentDashboardTab.id) {
                this.$nextTick(() => {
                    this.$router
                        .replace({
                            path: this.$route.path,
                            query: {
                                ...this.$route.query,
                                tab: this.currentDashboardTab.id,
                                dashboard: this.currentDashboard.id,
                            },
                        })
                        .catch((err) => {});
                });
            }
        },
        async deleteDashboardTab(id) {
            try {
                await this.$store.dispatch('dashboard/deleteDashboardTab', {
                    id,
                });
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.errorToast({ text: generic || 'Could not delete dashboard tab.' });
            }
        },
        async deleteCurrentDashboardTabForRemove() {
            if (this.currentDashboardTabForRemoveID) {
                await this.deleteDashboardTab(this.currentDashboardTabForRemoveID);
                this.currentDashboardTabForRemoveID = null;
            }
        },
        isLargeScreen,
        generateWidgetID,
        createDashboardTab(values) {
            return API.post(DASHBOARD_DASHBOARD_TAB_LIST_ENDPOINT(), values);
        },
        handleLayoutReady() {
            this.ready = true;
        },
        resizeEmitEvent(...args) {
            this.$refs.grid_layout.resizeEvent(...args);
        },
        handleLayoutUpdated: debounce(async function (tabWidgets) {
            if (this.isLargeScreenOnLoad && this.isLargeScreen()) {
                try {
                    this.$store.dispatch('dashboard/updateDashboardTabWidgets', tabWidgets);
                } catch (err) {
                    this.errorToast({ text: 'Could not update dashboard tab widgets.' });
                }
            }
        }, 1000),
        handleAddTab() {
            this.$bvModal.show(this.detailsDashboardTabModalID);
        },
        handleRemoveTab(tab) {
            this.currentDashboardTabForRemoveID = tab.id;
            this.$bvModal.show(this.removeDashboardTabModalID);
        },
        handleSuccessDashboardTabCreate(tab) {
            this.detailsDashboardTabModalKey += 1;
            this.$store.commit('dashboard/createdDashboardTab', tab);
        },
        async handleSuccessDashboardTabWidgetCreate() {
            await this.fetchDashboard();
        },
        async handleSuccessTabWidgetSettingsUpdate() {
            await this.fetchDashboard();
        },
    },
    watch: {
        currentRouteTab(id) {
            if (!this.currentDashboardTab || this.currentDashboardTab.id !== id) {
                this.$store.dispatch('dashboard/setCurrentDashboardTab', { id });
            }
            if (this.currentDashboardTab && this.currentDashboardTab.id) {
                this.$router
                    .push({
                        path: this.$route.path,
                        query: {
                            ...this.$route.query,
                            tab: this.currentDashboardTab.id,
                            dashboard: this.currentDashboard.id,
                        },
                    })
                    .catch((err) => {});
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.dashboard-page {
    display: flex;
    flex-direction: column;
}

.unselectable {
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;

    :deep(.ag-cell) {
        -webkit-user-select: none;
        -moz-user-select: none;
        user-select: none;
    }
}

.add-widget-card {
    border-style: dashed;
    border-color: $card-border-color;
    border-width: 3px;
    border-radius: $card-border-radius;

    .btn {
        padding: 1rem 1.2rem;
    }
}

:deep(.guest-name-status-column) {
    .ag-header-cell-label {
        padding-left: 33px;
    }
}
</style>
